import { daysUntil, formatDate, relativeDays, toDate } from "@/composables/dateFormat";
import { findLink } from "@/composables/participantStatus";

// Helpers for the member side of the start screen. Like participants, member groups
// expose their possible next steps as hypermedia links ("activate", "deactivate"),
// so a group waiting for release is recognised by its activate link.

export const ACTIVE_STATUS = "ACTIVE";
export const DISABLED_STATUS = "DISABLED";

// Club value of members without an associated second club.
export const NO_CLUB = "NONE";

// Display texts from the memberservice configuration (GET /config).
export function memberTypeText(config, memberType) {
  return config?.memberType?.[memberType] ?? memberType ?? "–";
}

export function clubText(config, club) {
  return club && club !== NO_CLUB ? config?.clubs?.[club] ?? club : "–";
}

export function hasClub(member) {
  return Boolean(member?.club) && member.club !== NO_CLUB;
}

// Roles that may manage member groups (Mitgliedergruppen, Jährliche Abrechnung).
export const MEMBER_ADMIN_ROLES = ["Admin"];

// Membership fees are always collected on 31 May.
const FEE_COLLECTION_MONTH = 4; // zero-based: May
const FEE_COLLECTION_DAY = 31;

export const findGroupLink = findLink;

export function isDisabled(entry) {
  return entry?.status === DISABLED_STATUS;
}

// Registered groups that still have to be activated; deactivated groups also carry
// an activate link, but they have left the club and are not waiting for release.
export function needsActivation(memberGroup) {
  return !isDisabled(memberGroup) && findGroupLink(memberGroup, "activate") !== null;
}

export function pendingMemberGroups(memberGroups) {
  return (memberGroups ?? []).filter(needsActivation);
}

/**
 * Label and tone for the status pill of a member group, in the same tones as
 * participantStatus: "open" waits for release, "ok" is active, "muted" has left.
 */
export function memberGroupStatus(memberGroup) {
  if (isDisabled(memberGroup)) {
    return { label: "Deaktiviert", tone: "muted" };
  }
  if (needsActivation(memberGroup)) {
    return { label: "Freigabe offen", tone: "open" };
  }
  if (memberGroup?.status === ACTIVE_STATUS) {
    return { label: "Aktiv", tone: "ok" };
  }
  return { label: memberGroup?.status || "Unbekannt", tone: "muted" };
}

export function memberGroupName(memberGroup) {
  return personName(memberGroup?.contactPerson ?? memberGroup?.members?.[0]);
}

// The member registration on the homepage does not send a family flag, so groups
// waiting for release usually have `family` unset. An explicit flag always wins;
// only when it is missing does a group with more than one person count as family.
export function isFamilyGroup(memberGroup) {
  const family = memberGroup?.family;
  if (family !== null && family !== undefined) return Boolean(family);
  return (memberGroup?.members?.length ?? 0) > 1;
}

// Second line of a to-do entry, e.g. "Familie · 4 Personen" or "Einzelmitglied".
export function memberGroupMeta(memberGroup) {
  if (!isFamilyGroup(memberGroup)) return "Einzelmitglied";
  const count = memberGroup?.members?.length ?? 0;
  return `Familie · ${count} ${count === 1 ? "Person" : "Personen"}`;
}

function personName(person) {
  return [person?.firstname, person?.lastname].filter(Boolean).join(" ") || "Unbekannt";
}

function leaveTime(member) {
  const date = toDate(member?.leaveDate);
  return date ? date.getTime() : null;
}

function hasLeft(member) {
  return isDisabled(member) || leaveTime(member) !== null;
}

/**
 * Members who have left, most recent leave date first. The memberservice sets the
 * leave date automatically when a member or its whole group is disabled. Members of
 * one group who left on the same day are combined into a single entry.
 */
export function recentExits(memberGroups, limit = 5) {
  const buckets = new Map();
  (memberGroups ?? []).forEach((group) => {
    (group.members ?? []).filter(hasLeft).forEach((member) => {
      const time = leaveTime(member);
      const key = `${group.id}|${time ?? "unknown"}`;
      if (!buckets.has(key)) {
        buckets.set(key, { group, time, members: [] });
      }
      buckets.get(key).members.push(member);
    });
  });

  const exits = Array.from(buckets.values()).map(({ group, time, members }) => {
    const together = members.length > 1;
    return {
      id: together ? `group-${group.id}-${time}` : `member-${members[0].id}`,
      name: together ? memberGroupName(group) : personName(members[0]),
      count: members.length,
      family: isFamilyGroup(group),
      leaveDate: time === null ? null : new Date(time),
      sortId: Math.max(...members.map((member) => Number(member.id) || 0)),
    };
  });

  // Newest leave date first; entries without a date (older data) go last.
  exits.sort((a, b) => {
    const aTime = a.leaveDate?.getTime() ?? Number.NEGATIVE_INFINITY;
    const bTime = b.leaveDate?.getTime() ?? Number.NEGATIVE_INFINITY;
    return bTime - aTime || b.sortId - a.sortId;
  });
  return exits.slice(0, limit);
}

// Number of members whose leave date lies in the given calendar year.
export function exitsInYear(memberGroups, year) {
  return (memberGroups ?? [])
    .flatMap((group) => group.members ?? [])
    .filter((member) => toDate(member.leaveDate)?.getFullYear() === year).length;
}

// The next 31 May that is today or later.
export function nextFeeCollectionDate(today = new Date()) {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const thisYear = new Date(start.getFullYear(), FEE_COLLECTION_MONTH, FEE_COLLECTION_DAY);
  return thisYear >= start ? thisYear : new Date(start.getFullYear() + 1, FEE_COLLECTION_MONTH, FEE_COLLECTION_DAY);
}

// Next fee collection with its countdown, e.g. "31.05.2027 · in 213 Tagen".
export function feeCollection(today = new Date()) {
  const date = nextFeeCollectionDate(today);
  const days = daysUntil(date, today);
  return { date, year: date.getFullYear(), days, dateLine: `${formatDate(date)} · ${relativeDays(days)}` };
}
